import {
	Button,
	ButtonGroup,
	FileTrigger,
	SearchField,
	TextField,
} from '@react-spectrum/s2';
import { memo, useCallback, useMemo, useState } from 'react';

import { fileUploadService } from '@/api/fileUploadApi';
import { useUserId } from '@/auth/UserIdContext';
import { useAudiences } from '@/hooks/useAudiences';
import { cn } from '@/utils/cn';
import {
	CONTACTS_ACCEPTED_FILE_TYPES,
	EMAIL_REGEX,
	ignoredSheetsNotice,
	MAX_CSV_SIZE,
	parseCsvText,
	readContactsFileAsCsv,
} from '@/utils/contactsCsv';

import type { AudienceSummary } from '../../../../bridge/types/action.ts';
import type { EmailRecipient } from '../../../../bridge/types/artifact.ts';

export type { AudienceSummary } from '../../../../bridge/types/action.ts';

type EmailRecipientsProps = {
	recipients: EmailRecipient[];
	audienceSummaries?: AudienceSummary[];
	isReadOnly?: boolean;
	onRecipientsChange?: (recipients: EmailRecipient[]) => void;
	onAudienceSummariesChange?: (summaries: AudienceSummary[]) => void;
};

type CsvSource = Extract<EmailRecipient['source'], { type: 'csv' }>;

function cloneRecipients(recipients: EmailRecipient[]): EmailRecipient[] {
	return recipients.map((recipient) => ({
		...recipient,
		source: { ...recipient.source },
	}));
}

function recipientKey(recipient: EmailRecipient): string {
	let source: string;
	if (recipient.source.type === 'csv') {
		source = `csv:${recipient.source.filePath}`;
	} else if (recipient.source.type === 'audience') {
		source = `audience:${recipient.source.audienceId}`;
	} else {
		source = 'manual';
	}
	return `${recipient.email.toLowerCase()}|${source}`;
}

function sourceLabel(recipient: EmailRecipient): string {
	if (recipient.source.type === 'csv') {
		return `CSV: ${recipient.source.filename}`;
	}
	if (recipient.source.type === 'audience') {
		return `Audience: ${recipient.source.audienceName}`;
	}
	return 'Manual';
}

function uniqueCsvSources(recipients: EmailRecipient[]): CsvSource[] {
	const sources = new Map<string, CsvSource>();
	for (const recipient of recipients) {
		if (recipient.source.type === 'csv') {
			sources.set(recipient.source.filePath, recipient.source);
		}
	}
	return [...sources.values()];
}

function mergeRecipients(
	current: EmailRecipient[],
	incoming: EmailRecipient[],
): EmailRecipient[] {
	const seen = new Set(
		current.map((recipient) => recipient.email.toLowerCase()),
	);
	const next = [...current];
	for (const recipient of incoming) {
		const emailKey = recipient.email.toLowerCase();
		if (seen.has(emailKey)) {
			continue;
		}
		seen.add(emailKey);
		next.push(recipient);
	}
	return next;
}

export const EmailRecipients = memo(function EmailRecipients({
	recipients: recipientsProp,
	audienceSummaries: audienceSummariesProp = [],
	isReadOnly = false,
	onRecipientsChange,
	onAudienceSummariesChange,
}: EmailRecipientsProps) {
	const userId = useUserId();
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState<string | null>(null);
	const [csvError, setCsvError] = useState<string | null>(null);
	const [csvNotice, setCsvNotice] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);

	// Audience picker state
	const [showAudiencePicker, setShowAudiencePicker] = useState(false);
	const [audienceError, setAudienceError] = useState<string | null>(null);
	const [pickerSearch, setPickerSearch] = useState('');
	const {
		audiences,
		isLoading: isLoadingAudiences,
		hasMore: hasMoreAudiences,
		loadMore: loadMoreAudiences,
		isLoadingMore: isLoadingMoreAudiences,
	} = useAudiences(showAudiencePicker ? (userId ?? undefined) : undefined);

	const recipients = useMemo(
		() => cloneRecipients(recipientsProp),
		[recipientsProp],
	);
	const csvSources = useMemo(() => uniqueCsvSources(recipients), [recipients]);
	const addedAudienceIds = useMemo(
		() => new Set(audienceSummariesProp.map((s) => s.audienceId)),
		[audienceSummariesProp],
	);
	const filteredAudiences = useMemo(() => {
		if (!pickerSearch.trim()) return audiences;
		const query = pickerSearch.toLowerCase();
		return audiences.filter((a) => a.name.toLowerCase().includes(query));
	}, [audiences, pickerSearch]);

	const commit = useCallback(
		(nextRecipients: EmailRecipient[]) => {
			onRecipientsChange?.(nextRecipients);
		},
		[onRecipientsChange],
	);

	const removeRecipient = useCallback(
		(target: EmailRecipient) => {
			const key = recipientKey(target);
			commit(recipients.filter((recipient) => recipientKey(recipient) !== key));
		},
		[commit, recipients],
	);

	const removeCsv = useCallback(
		(filePath: string) => {
			commit(
				recipients.filter(
					(recipient) =>
						recipient.source.type !== 'csv' ||
						recipient.source.filePath !== filePath,
				),
			);
		},
		[commit, recipients],
	);

	const removeAudience = useCallback(
		(audienceId: string) => {
			onAudienceSummariesChange?.(
				audienceSummariesProp.filter((s) => s.audienceId !== audienceId),
			);
		},
		[audienceSummariesProp, onAudienceSummariesChange],
	);

	const handleAddAudience = useCallback(
		(
			audienceId: string,
			audienceName: string,
			memberCount: number,
			hasValidEmail: boolean,
		) => {
			if (addedAudienceIds.has(audienceId) || !userId) return;
			setAudienceError(null);
			if (memberCount === 0) {
				setAudienceError(`"${audienceName}" has no members.`);
				return;
			}
			if (!hasValidEmail) {
				setAudienceError(
					`No members in "${audienceName}" have a valid email address.`,
				);
				return;
			}
			onAudienceSummariesChange?.([
				...audienceSummariesProp,
				{ audienceId, audienceName, memberCount, hasValidEmail },
			]);
		},
		[
			addedAudienceIds,
			audienceSummariesProp,
			onAudienceSummariesChange,
			userId,
		],
	);

	const addManualRecipient = useCallback(() => {
		const trimmedEmail = email.trim();
		if (!trimmedEmail) {
			setEmailError('Email is required');
			return;
		}
		if (!EMAIL_REGEX.test(trimmedEmail)) {
			setEmailError('Invalid email address');
			return;
		}
		if (
			recipients.some(
				(recipient) =>
					recipient.email.toLowerCase() === trimmedEmail.toLowerCase(),
			)
		) {
			setEmailError('This email has already been added');
			return;
		}

		commit([
			...recipients,
			{
				name: name.trim(),
				email: trimmedEmail,
				source: { type: 'manual' },
			},
		]);
		setName('');
		setEmail('');
		setEmailError(null);
	}, [commit, email, name, recipients]);

	const handleCsvUpload = useCallback(
		async (file: File) => {
			if (!userId || isUploading) {
				return;
			}
			if (file.size > MAX_CSV_SIZE) {
				setCsvError('File size exceeds 5MB limit');
				return;
			}

			setIsUploading(true);
			setCsvError(null);
			setCsvNotice(null);
			try {
				const { csv, ignoredSheetNames } = await readContactsFileAsCsv(file);
				const parsed = parseCsvText(csv);
				if (parsed.error) {
					setCsvError(parsed.error);
					return;
				}
				setCsvNotice(ignoredSheetsNotice(ignoredSheetNames));
				const result = await fileUploadService.upload(file, userId);
				const source: CsvSource = {
					type: 'csv',
					filename: result.fileName,
					filePath: result.url,
				};
				commit(
					mergeRecipients(
						recipients,
						parsed.recipients.map((recipient) => ({
							...recipient,
							source,
						})),
					),
				);
			} catch (error) {
				setCsvError(error instanceof Error ? error.message : 'Upload failed');
			} finally {
				setIsUploading(false);
			}
		},
		[commit, isUploading, recipients, userId],
	);

	if (isReadOnly) {
		if (recipients.length === 0 && audienceSummariesProp.length === 0) {
			return (
				<p className="text-sm text-(--text-muted)">No recipients added.</p>
			);
		}
		return (
			<div className="flex flex-col gap-1">
				{audienceSummariesProp.map((summary) => (
					<div
						key={summary.audienceId}
						className="flex items-baseline gap-2 py-0.5"
					>
						<span className="text-sm font-medium text-(--text-primary)">
							Audience: {summary.audienceName}
						</span>
						{summary.memberCount != null && (
							<span className="text-xs text-(--text-muted)">
								{summary.memberCount} member
								{summary.memberCount !== 1 ? 's' : ''}
							</span>
						)}
					</div>
				))}
				{recipients.map((recipient) => (
					<div
						key={recipientKey(recipient)}
						className="flex items-baseline gap-2 py-0.5"
					>
						<span className="text-sm font-medium text-(--text-primary)">
							{recipient.name || recipient.email}
						</span>
						{recipient.name && (
							<span className="text-xs text-(--text-muted)">
								{recipient.email}
							</span>
						)}
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-2">
				<div className="self-start">
					<ButtonGroup>
						<FileTrigger
							acceptedFileTypes={CONTACTS_ACCEPTED_FILE_TYPES}
							onSelect={(fileList) => {
								if (fileList?.[0]) void handleCsvUpload(fileList[0]);
							}}
						>
							<Button
								variant="secondary"
								fillStyle="outline"
								isDisabled={!userId || isUploading}
							>
								+ Upload CSV/Excel
							</Button>
						</FileTrigger>
						<Button
							variant="secondary"
							fillStyle="outline"
							isDisabled={!userId}
							onPress={() => {
								setShowAudiencePicker((prev) => {
									if (!prev) {
										setPickerSearch('');
										setAudienceError(null);
									}
									return !prev;
								});
							}}
						>
							{showAudiencePicker ? 'Close Audiences' : '+ Add Audience'}
						</Button>
					</ButtonGroup>
				</div>
			</div>

			<div className="flex flex-col gap-2">
				{csvError && <p className="m-0 text-sm text-red-600">{csvError}</p>}
				{csvNotice && !csvError && (
					<p
						className="m-0 rounded-lg border border-amber-200 bg-amber-50 px-3
							py-2 text-sm text-amber-900"
					>
						{csvNotice}
					</p>
				)}
				{csvSources.length > 0 && (
					<div className="flex flex-col gap-2">
						{csvSources.map((source) => (
							<div
								key={source.filePath}
								className="flex items-center justify-between gap-2 rounded-md
									border border-(--glass-border) px-3 py-2"
							>
								<span className="truncate text-sm">{source.filename}</span>
								<Button
									variant="secondary"
									size="S"
									onPress={() => removeCsv(source.filePath)}
								>
									Remove CSV
								</Button>
							</div>
						))}
					</div>
				)}
				{audienceSummariesProp.length > 0 && (
					<div className="flex flex-col gap-2">
						{audienceSummariesProp.map((summary) => (
							<div
								key={summary.audienceId}
								className="flex items-center justify-between gap-2 rounded-md
									border border-(--glass-border) px-3 py-2"
							>
								<span className="truncate text-sm">
									Audience: {summary.audienceName}
									{summary.memberCount != null &&
										` (${summary.memberCount} member${summary.memberCount !== 1 ? 's' : ''})`}
								</span>
								<Button
									variant="secondary"
									size="S"
									onPress={() => removeAudience(summary.audienceId)}
								>
									Remove Audience
								</Button>
							</div>
						))}
					</div>
				)}
			</div>

			{showAudiencePicker && (
				<div className="glass-card-sm flex flex-col gap-3 p-4">
					<SearchField
						aria-label="Search audiences"
						placeholder="Search by name..."
						value={pickerSearch}
						onChange={setPickerSearch}
					/>

					{isLoadingAudiences ? (
						<p className="py-4 text-center text-sm text-gray-500">
							Loading audiences...
						</p>
					) : filteredAudiences.length === 0 ? (
						<p className="py-4 text-center text-sm text-gray-500">
							{pickerSearch
								? 'No audiences match your search'
								: 'No audiences available'}
						</p>
					) : (
						<div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
							{filteredAudiences.map((audience) => {
								const isAdded = addedAudienceIds.has(audience.id);
								return (
									<button
										key={audience.id}
										type="button"
										disabled={isAdded}
										onClick={() =>
											handleAddAudience(
												audience.id,
												audience.name,
												audience.member_count ?? 0,
												audience.reachability?.has_valid_email ?? true,
											)
										}
										className={cn(
											`flex items-center justify-between rounded-md px-3 py-2
												text-left text-sm transition-colors`,
											isAdded
												? 'cursor-default opacity-50'
												: 'hover:bg-(--glass-bg-hover)',
										)}
									>
										<div className="min-w-0">
											<span className="block truncate text-(--text-primary)">
												{audience.name}
											</span>
											{audience.member_count != null && (
												<span className="block truncate text-xs text-gray-500">
													{audience.member_count} member
													{audience.member_count !== 1 ? 's' : ''}
												</span>
											)}
										</div>
										{isAdded && (
											<span className="shrink-0 text-xs text-gray-500">
												Added
											</span>
										)}
									</button>
								);
							})}
						</div>
					)}

					{audienceError && (
						<p className="m-0 text-sm text-red-600">{audienceError}</p>
					)}

					{hasMoreAudiences && (
						<div className="flex justify-center">
							<Button
								variant="secondary"
								size="S"
								isPending={isLoadingMoreAudiences}
								onPress={loadMoreAudiences}
							>
								Load more audiences
							</Button>
						</div>
					)}
				</div>
			)}

			<div className="flex flex-col gap-2">
				<div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
					<TextField label="Name" value={name} onChange={setName} />
					<TextField
						label="Email"
						value={email}
						onChange={(value) => {
							setEmail(value);
							setEmailError(null);
						}}
						isRequired
						isInvalid={emailError !== null}
						errorMessage={emailError ?? undefined}
					/>
					<Button variant="accent" onPress={addManualRecipient}>
						Add
					</Button>
				</div>
			</div>

			{recipients.length > 0 && (
				<div className="flex flex-col gap-1">
					<div
						className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 px-1 text-xs
							font-semibold text-(--text-muted)"
					>
						<span>Name</span>
						<span>Email</span>
						<span>Source</span>
						<span />
					</div>
					{recipients.map((recipient) => (
						<div
							key={recipientKey(recipient)}
							className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2
								px-1 py-1"
						>
							<span className="truncate text-sm">
								{recipient.name || 'Unnamed'}
							</span>
							<span className="truncate text-sm">{recipient.email}</span>
							<span className="truncate text-sm">{sourceLabel(recipient)}</span>
							<Button
								variant="secondary"
								size="S"
								onPress={() => removeRecipient(recipient)}
							>
								Remove
							</Button>
						</div>
					))}
				</div>
			)}
		</div>
	);
});
